import React, { useEffect, useRef, useState } from 'react';
import { Globe, MapPin, Navigation, X } from 'lucide-react';
import { Place } from '../data/places';
import { BlattFuss, BlattHintergrund, BlattKopf, amRechner } from './FilterBlatt';

/**
 * Feld „Ort“ der Suchzeile am Rechner (27.10.2026). Öffnet ein kleines Menü statt des Karten-Dialogs:
 * die Karte steht dort ohnehin rechts neben der Liste und zeigt den Umkreis, sobald ein Ort gesetzt
 * ist. Eine zweite Karte im Fenster darüber war doppelt.
 *
 * Im Menü: Stadt, PLZ oder Region eintippen (mit Vorschlägen), „In meiner Nähe“ und der Umkreis.
 * Entfernt wird der Ort mit dem X im Feld. Gerechnet wird nichts hier; die Seite setzt Ort und Umkreis.
 *
 * Länder (28.10.2026): Deutschland, Österreich und die Schweiz stehen vor der Eingabe zur Wahl und
 * werden beim Tippen gefunden; vorher ging das nur über „Filter“. Eine vierstellige PLZ kann in
 * Österreich oder der Schweiz liegen, deshalb kommen dann zwei Vorschläge.
 */
export interface OrtLand { key: string; label: string }
/** Länder mit vierstelliger PLZ; Deutschland hat fünf Stellen. */
const PLZ4_LAENDER = ['AT', 'CH'];
interface Props {
  label: string;
  value: string;
  active: boolean;
  /** Ort mit Umkreis gesetzt: erst dann zählt der Umkreis. */
  hatUmkreis: boolean;
  radius: number | null;
  radien: number[];
  ortet?: boolean;
  /** Vorschläge zur Eingabe (Orte leer, solange die Ortsliste noch lädt). */
  vorschlaege: (q: string) => { orte: Place[]; regionen: { key: string; label: string }[] };
  onOeffnen: () => void;
  onOrt: (p: Place) => void;
  onRegion: (key: string) => void;
  /** `land` nur bei vierstelliger PLZ (AT oder CH). */
  onPlz: (plz: string, land?: string) => void;
  /** Wählbare Länder (Deutschland, Österreich, Schweiz). */
  laender: OrtLand[];
  onLand: (key: string) => void;
  onNaehe: () => void;
  onRadius: (km: number) => void;
  onUeberall: () => void;
  /** Als kleiner Knopf (Handy) statt als großes Feld. */
  pille?: boolean;
  /** Trefferzahl für den Knopf unten im Blatt (Handy). */
  treffer?: number;
}

const OrtFeld: React.FC<Props> = ({ label, value, active, hatUmkreis, radius, radien, ortet, vorschlaege, onOeffnen, onOrt, onRegion, onPlz, laender, onLand, onNaehe, onRadius, onUeberall, pille, treffer }) => {
  const [offen, setOffen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const eingabe = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!offen) return;
    if (amRechner()) eingabe.current?.focus();
    const zu = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOffen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOffen(false); };
    document.addEventListener('mousedown', zu);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', zu); document.removeEventListener('keydown', esc); };
  }, [offen]);

  const t = q.trim();
  const istPlz = /^\d{4,5}$/.test(t);
  const plzLaender = istPlz && t.length === 4 ? laender.filter(l => PLZ4_LAENDER.includes(l.key)) : [];
  const { orte, regionen } = t.length >= 2 && !istPlz ? vorschlaege(t) : { orte: [], regionen: [] };
  const norm = (x: string) => x.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const passendeLaender = t.length >= 2 && !istPlz ? laender.filter(l => norm(l.label).startsWith(norm(t))) : [];
  const plzWaehlen = (land?: string) => fertig(() => onPlz(t, land));
  const fertig = (fn: () => void) => { fn(); setQ(''); };

  return (
    <div ref={ref} className={pille ? 'filter-pille-wrap' : 'such-feld-wrap'}>
      {pille ? (
        <button type="button" className={`filter-pille${active ? ' filter-pille--an' : ''}`} aria-expanded={offen} aria-haspopup="dialog"
          onClick={() => { if (!offen) onOeffnen(); setOffen(o => !o); }}>
          {active ? (hatUmkreis && radius ? `${value} · ${radius} km` : value) : 'Ort'}
        </button>
      ) : (
        <button type="button" className={`such-feld${active ? ' such-feld--aktiv' : ''}`} aria-expanded={offen} aria-haspopup="dialog"
          onClick={() => { if (!offen) onOeffnen(); setOffen(o => !o); }}>
          <span className="such-feld-icon" aria-hidden><MapPin size={18} /></span>
          <span className="such-feld-text">
            <span className="such-feld-label">{label}</span>
            <span className="such-feld-wert">{value}</span>
          </span>
        </button>
      )}
      {active && (
        <button type="button" className={pille ? 'filter-pille-x' : 'such-feld-x'} onClick={() => { onUeberall(); setOffen(false); }} aria-label="Ort entfernen"><X size={pille ? 14 : 16} aria-hidden /></button>
      )}
      {offen && <BlattHintergrund onClose={() => setOffen(false)} />}
      {offen && (
        <div className="ort-menue" role="dialog" aria-label="Ort und Umkreis">
          <BlattKopf titel="Ort" onClose={() => setOffen(false)} />
          <div className="ort-eingabe">
            <MapPin size={16} aria-hidden />
            <input ref={eingabe} value={q} onChange={e => setQ(e.target.value)} placeholder="Stadt, PLZ, Region oder Bundesland"
              aria-label="Stadt, PLZ, Region oder Bundesland"
              onKeyDown={e => {
                if (e.key !== 'Enter') return;
                if (istPlz) plzWaehlen(plzLaender[0]?.key);
                else if (passendeLaender[0] && !orte.length) fertig(() => onLand(passendeLaender[0].key));
                else if (orte[0]) fertig(() => onOrt(orte[0]));
                else if (regionen[0]) fertig(() => onRegion(regionen[0].key));
              }} />
            {q && <button type="button" className="ort-leeren" onClick={() => setQ('')} aria-label="Eingabe leeren"><X size={14} aria-hidden /></button>}
          </div>

          {(istPlz || orte.length > 0 || regionen.length > 0 || passendeLaender.length > 0) ? (
            <ul className="ort-liste">
              {istPlz && t.length === 5 && <li><button type="button" onClick={() => plzWaehlen()}><MapPin size={15} aria-hidden />PLZ {t}<small>Deutschland</small></button></li>}
              {plzLaender.map(l => (
                <li key={l.key}><button type="button" onClick={() => plzWaehlen(l.key)}><MapPin size={15} aria-hidden />PLZ {t}<small>{l.label}</small></button></li>
              ))}
              {passendeLaender.map(l => (
                <li key={l.key}><button type="button" onClick={() => fertig(() => onLand(l.key))}><Globe size={15} aria-hidden />{l.label}<small>Land</small></button></li>
              ))}
              {orte.map(p => (
                <li key={`${p.name}-${p.lat}`}><button type="button" onClick={() => fertig(() => onOrt(p))}><MapPin size={15} aria-hidden />{p.name}{p.state && <small>{p.state}</small>}</button></li>
              ))}
              {regionen.map(r => (
                <li key={r.key}><button type="button" onClick={() => fertig(() => onRegion(r.key))}><span className="ort-region" aria-hidden />{r.label}<small>Region</small></button></li>
              ))}
            </ul>
          ) : (
            <ul className="ort-liste">
              <li><button type="button" onClick={onNaehe} disabled={ortet}><Navigation size={15} aria-hidden />{ortet ? 'Suche Standort …' : 'In meiner Nähe'}</button></li>
              {laender.map(l => (
                <li key={l.key}><button type="button" onClick={() => { onLand(l.key); setOffen(false); }}><Globe size={15} aria-hidden />{l.label}<small>Land</small></button></li>
              ))}
            </ul>
          )}

          {hatUmkreis && (
            <div className="ort-umkreis">
              <span>Umkreis</span>
              <div role="group" aria-label="Umkreis">
                {radien.map(km => (
                  <button key={km} type="button" aria-pressed={radius === km} className={radius === km ? 'ort-km ort-km--an' : 'ort-km'} onClick={() => onRadius(km)}>{km} km</button>
                ))}
              </div>
            </div>
          )}
          <BlattFuss treffer={treffer} onZuruecksetzen={active ? () => { onUeberall(); setQ(''); } : undefined} onFertig={() => setOffen(false)} />
        </div>
      )}
    </div>
  );
};

export default OrtFeld;
